import React from 'react';
import { MxButton } from '@guildeducationinc/playground-mx';
import { Typography } from '@guildeducationinc/recess';
import {
	EligibilitySummaryCategoryStatus,
	useEligibilitySummaryQuery,
} from '@src/graphqlTypes/graphqlGatewayTypes';
import { buEligibility } from '@src/utilities/urlUtility';
import { Skeleton } from '@src/components/UserFacetSkeletonLoader';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import CloseIcon from '@guildeducationinc/recess/icons/CloseIcon';
import { GetMemberProgramQuery } from '@src/graphqlTypes/catalogServiceTypes';
import { EligibilityTags } from '../EligibilityTags';
import styles from './EligibilityHighlightPanel.module.scss';

interface EligibilityHighlightProps {
	programUuid: GetMemberProgramQuery['memberProgram']['uuid'];
}

export const EligibilityHighlightPanel: React.FC<EligibilityHighlightProps> = ({ programUuid }) => {
	const { userFromIdToken: authUser } = useMemberExperienceAuth();
	const { data, loading } = useEligibilitySummaryQuery({
		variables: {
			uuid: authUser?.id,
			employeeId: authUser?.employee_id,
			employerUuid: authUser?.employer_uuid,
		},
		context: { gqlServer: 'gateway' },
		skip: !authUser || !authUser.employee_id,
	});

	const employerName = authUser?.employer || 'your employer';
	const eligibilityCategories = data?.eligibilitySummary?.categories;
	const failingCatergories = eligibilityCategories?.filter(
		category => category.status === EligibilitySummaryCategoryStatus.FAIL
	);
	const failingTenureOnly =
		failingCatergories?.length === 1 && failingCatergories[0].categoryTag === 'tenure';
	const tenureDays = data?.eligibilitySummary?.metadata?.tenureDays;

	return (
		<Skeleton dataLoading={loading} height={200}>
			<div className={styles.eligibilityHighlightContainer}>
				<div className={failingTenureOnly ? styles.tenureHeadline : styles.generalHeadline}>
					<EligibilityTags
						failingTenureOnly={failingTenureOnly}
						programUuid={programUuid}
						tenureDays={tenureDays}
					/>
					<Typography
						className={styles.headlineText}
						data-testid='eligibility-headline'
						variant='h3'
					>
						You can&apos;t apply to this program right now
					</Typography>
				</div>

				<div className={styles.eligibilityHighlightContent}>
					{failingCatergories ? (
						<>
							{failingTenureOnly ? (
								<Typography testid='tenure-message' variant='title3'>
									{tenureDays
										? `You'll be able to apply once you've been with ${employerName} for ${tenureDays} days.`
										: `You'll be able to apply once you've completed ${employerName}'s tenure period.`}
								</Typography>
							) : (
								<>
									<Typography
										testid='employer-requirements-message'
										variant='title3'
									>{`You'll be able to apply once you meet ${employerName}'s requirements:`}</Typography>
									<ul className={styles.eligibilityReasons}>
										{failingCatergories.map(category => (
											<li className={styles.itemContainer} key={category.categoryTag}>
												<CloseIcon color='red' size={24} />
												<div className={styles.itemColumn}>
													<Typography
														className={styles.itemTitle}
														testid='category-title'
														variant='label1'
													>
														{category.categoryTitle}
													</Typography>
												</div>
											</li>
										))}
									</ul>
								</>
							)}
						</>
					) : (
						<Typography
							className='margin--bottom_16'
							testid='no-eligibility-reasons-message'
							variant='title3'
						>{`You'll be able to apply once you meet ${employerName}'s requirements.`}</Typography>
					)}

					<MxButton
						appearance='outline'
						className={failingTenureOnly ? 'margin--top_16' : null}
						fullWidth
						href={buEligibility}
					>
						View eligibility details
					</MxButton>
				</div>
			</div>
		</Skeleton>
	);
};
